import { KitButton } from './button';
import { useEffect, useRef } from 'react';
import type { FieldControlProps } from './form-field';

export function Checkbox({
  label,
  indeterminate = false,
  readOnly = false,
  ...props
}: FieldControlProps & { label: string; indeterminate?: boolean; readOnly?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  const { value, onValueChange, ...input } = props;
  return (
    <label
      className="pc-check"
      data-state={props.disabled ? 'disabled' : readOnly ? 'readonly' : undefined}
    >
      <input
        {...input}
        ref={ref}
        type="checkbox"
        checked={value === true}
        aria-checked={indeterminate ? 'mixed' : value === true}
        aria-readonly={readOnly}
        onClick={(event) => {
          if (readOnly) event.preventDefault();
        }}
        onChange={(event) => {
          if (!readOnly) onValueChange(event.currentTarget.checked);
        }}
      />
      <span className="pc-check-box" aria-hidden="true" />
      <span>{label}</span>
    </label>
  );
}

export function RadioGroup({
  options,
  label,
  ...props
}: FieldControlProps & {
  label: string;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div
      id={props.id}
      className="pc-choice-group"
      role="radiogroup"
      aria-label={label}
      aria-invalid={props['aria-invalid']}
      aria-describedby={props['aria-describedby']}
    >
      {options.map((option, index) => (
        <label
          key={option.value}
          className="pc-check"
          data-kind="radio"
          data-state={props.disabled ? 'disabled' : undefined}
        >
          <input
            type="radio"
            id={`${props.id}-${index}`}
            name={props.id}
            value={option.value}
            checked={props.value === option.value}
            disabled={props.disabled}
            onBlur={props.onBlur}
            onChange={() => props.onValueChange(option.value)}
          />
          <span className="pc-check-box" aria-hidden="true" />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
}

// Switch is an immediate-setting control. A submit form uses Checkbox instead.
export function Switch({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange(value: boolean): void;
}) {
  return (
    <label>
      <span>{label}</span>
      <KitButton
        type="button"
        className="pc-switch"
        role="switch"
        aria-label={label}
        aria-checked={checked}
        disabled={disabled}
        data-state={disabled ? 'disabled' : undefined}
        onClick={() => onChange(!checked)}
      >
        <span className="pc-switch-thumb" aria-hidden="true" />
      </KitButton>
    </label>
  );
}
